import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { deburr, orderBy } from "lodash";
import { useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import { getReportClientName } from "../utils";
import { OrdersReportData } from "./useOrdersReportData";

const INTEGER_NUM_FMT = "#,##0";

const m = defineMessages({
    clientNumber: { id: "OrdersReportExcel.ClientNumber", defaultMessage: "Client Number" },
    client: { id: "OrdersReportExcel.Client", defaultMessage: "Client" },
    country: { id: "OrdersReportExcel.Country", defaultMessage: "Country" },
    province: { id: "OrdersReportExcel.Province", defaultMessage: "Province/State" },
    city: { id: "OrdersReportExcel.City", defaultMessage: "City" },
    period: { id: "OrdersReportExcel.Period", defaultMessage: "Period" },
    totals: { id: "OrdersReportExcel.Totals", defaultMessage: "Totals" },
    currentPeriod: { id: "OrdersReportExcel.CurrentPeriod", defaultMessage: "Current" },
    previousPeriod: { id: "OrdersReportExcel.PreviousPeriod", defaultMessage: "Previous" },
    worksheetName: { id: "OrdersReportExcel.WorksheetName", defaultMessage: "Orders Report {yearPrevious}-{yearCurrent}" },
    filename: {
        id: "OrdersReportExcel.Filename",
        defaultMessage: "Orders Report {monthPrevious} {yearPrevious} - {monthCurrent} {yearCurrent}",
    },
});

export const useOrdersReportExcel = () => {
    const { formatMessage } = useIntl();

    const getClientAddressColumns = useCallback((clientCode: string, reportData: OrdersReportData) => {
        const clientInformation = reportData.clientInformationByClientCode[clientCode];
        return [
            clientInformation?.country ?? "",
            clientInformation?.province ?? "",
            clientInformation?.city ?? "",
        ];
    }, []);

    const getCurrentPeriodRow = useCallback(
        (clientCode: string, reportData: OrdersReportData) => {
            const monthlyOrders = reportData.monthlyOrdersByClientCode[clientCode];

            return [
                clientCode,
                getReportClientName(reportData.clientInformationByClientCode, clientCode),
                ...getClientAddressColumns(clientCode, reportData),
                formatMessage(m.currentPeriod),
                ...reportData.monthsInOrder.map(
                    (month) => monthlyOrders?.statsByMonth[month]?.currentPeriodTotal ?? 0,
                ),
                monthlyOrders?.currentPeriodTotal ?? 0,
            ];
        },
        [formatMessage, getClientAddressColumns],
    );

    const getPreviousPeriodRow = useCallback(
        (clientCode: string, reportData: OrdersReportData) => {
            const monthlyOrders = reportData.monthlyOrdersByClientCode[clientCode];

            return [
                clientCode,
                getReportClientName(reportData.clientInformationByClientCode, clientCode),
                ...getClientAddressColumns(clientCode, reportData),
                formatMessage(m.previousPeriod),
                ...reportData.monthsInOrder.map(
                    (month) => monthlyOrders?.statsByMonth[month]?.previousPeriodTotal ?? 0,
                ),
                monthlyOrders?.previousPeriodTotal ?? 0,
            ];
        },
        [formatMessage, getClientAddressColumns],
    );

    const getClientRows = useCallback(
        (clientCode: string, reportData: OrdersReportData) => {
            return [
                getCurrentPeriodRow(clientCode, reportData),
                getPreviousPeriodRow(clientCode, reportData),
            ];
        },
        [getCurrentPeriodRow, getPreviousPeriodRow],
    );

    const getWorksheetName = useCallback(
        (reportData: OrdersReportData) => {
            const yearPrevious = dayjs(reportData.previousPeriodStartDate).format("YYYY");
            const yearCurrent = dayjs(reportData.currentPeriodEndDate).format("YYYY");

            return formatMessage(m.worksheetName, {
                yearPrevious,
                yearCurrent,
            });
        },
        [formatMessage],
    );

    const addEntries = useCallback(
        (workbook: Workbook, reportData: OrdersReportData) => {
            const worksheet = workbook.addWorksheet(getWorksheetName(reportData));

            const columnsWidths = [13, 35, 15, 15, 15, 12, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11];

            worksheet.columns = columnsWidths.map((width) => ({ width }));

            const headers = [
                formatMessage(m.clientNumber),
                formatMessage(m.client),
                formatMessage(m.country),
                formatMessage(m.province),
                formatMessage(m.city),
                formatMessage(m.period),
                ...reportData.monthsInOrder.map((month) =>
                    dayjs().month(month).format("MMM").toUpperCase(),
                ),
                formatMessage(m.totals),
            ];

            const headerRow = worksheet.addRow(headers);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFE0E0E0" },
                };
            });

            const orderedClientCodes = orderBy(reportData.clientCodes, (clientCode) =>
                deburr(getReportClientName(reportData.clientInformationByClientCode, clientCode).toLowerCase()),
            );

            const clientRowsData = orderedClientCodes.flatMap((clientCode) =>
                getClientRows(clientCode, reportData),
            );

            worksheet.addRows(clientRowsData);

            const firstMonthColumnIndex = 7;
            for (
                let rowIndex = headerRow.number + 1;
                rowIndex <= headerRow.number + clientRowsData.length;
                rowIndex++
            ) {
                const row = worksheet.getRow(rowIndex);
                for (let col = firstMonthColumnIndex; col <= headers.length; col++) {
                    row.getCell(col).numFmt = INTEGER_NUM_FMT;
                }
            }

            worksheet.autoFilter = {
                from: {
                    row: 1,
                    column: 1,
                },
                to: {
                    row: clientRowsData.length + 1,
                    column: headers.length,
                },
            };
        },
        [formatMessage, getClientRows, getWorksheetName],
    );

    const getFilename = useCallback(
        (reportData: OrdersReportData) => {
            const yearPrevious = dayjs(reportData.previousPeriodStartDate).format("YYYY");
            const monthPrevious = dayjs(reportData.previousPeriodStartDate).format("MMMM");
            const yearCurrent = dayjs(reportData.currentPeriodEndDate).format("YYYY");
            const monthCurrent = dayjs(reportData.currentPeriodEndDate).format("MMMM");

            return formatMessage(m.filename, {
                monthPrevious,
                yearPrevious,
                monthCurrent,
                yearCurrent,
            });
        },
        [formatMessage],
    );

    const exportToExcelAndDownload = useCallback(
        async (ordersReportData: OrdersReportData) => {
            const filename = `${getFilename(ordersReportData)}.xlsx`;

            const workbook = new Workbook();
            addEntries(workbook, ordersReportData);

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.setAttribute("href", url);
            a.setAttribute("download", filename);
            a.click();
            window.URL.revokeObjectURL(url);
        },
        [addEntries, getFilename],
    );

    return { exportToExcelAndDownload };
};
